/**
 * US C1 — filterable submission list (renderSubmissionList) + the "create a new
 * submission" flow (renderSubmissionNew), which is US B1: pick the subject (household, or
 * Đại lý/MM for the RCP MM form), then a form type valid for it, then — per `FORM_SUBJECT`
 * — a plot (required / optional / forbidden), independent of whether any other form type
 * has ever been filled in for that subject (R2).
 */
import * as api from '../api-client.js';
import { FORM_KEYS, FORM_LABELS, FORM_SUBJECT, formLabel } from '@farmer/audit-rules';
import { navigate, buildHash, parseHash, debounce, el, esc, formatDate, formatDateTime, statusBadge, toast } from '../ui/util.js';
import { openFormDialog } from '../ui/form-dialog.js';

/** Chủ thể của phiếu: tên Hộ, hoặc "MM-001 · Tên MM" với phiếu của Đại lý/MM (Doc/09 lát 2). */
function subjectText(s) {
  if (s.middlemanId) return `${s.middlemanCode || ''} · ${s.middlemanName || s.middlemanId}`;
  return s.householdName || s.householdId || '—';
}

export async function renderSubmissionList(container) {
  const user = api.getCurrentUser();
  const QUAN_LY = ['supervisor', 'instance_admin', 'admin'];
  const canExport = !!user && QUAN_LY.includes(user.role);
  /** Chỉ admin hệ thống thấy nhiều đơn vị nên chỉ họ cần bộ lọc này. */
  const laAdminHeThong = user?.role === 'admin';
  let donVis = [];
  if (laAdminHeThong) {
    try {
      donVis = await api.instances.list();
    } catch (err) {
      console.warn('[submissions] không tải được danh sách đơn vị', err);
    }
  }

  container.innerHTML = `
    <div class="page-head">
      <div><h1>Phiếu điều tra</h1><p>Danh sách phiếu theo trạng thái, loại phiếu, cảnh báo audit (US C1).</p></div>
      <div class="page-head-actions">
        ${canExport ? `<button class="btn btn-tonal" id="exportCsvBtn"><span class="ms">table_view</span>Xuất CSV</button>
        <button class="btn btn-tonal" id="exportXlsxBtn"><span class="ms">download</span>Xuất XLSX</button>` : ''}
        <a class="btn" href="${buildHash('/submissions/new')}"><span class="ms">note_add</span>Tạo phiếu mới</a>
      </div>
    </div>
    <div class="filters-bar">
      <input type="search" id="fSearch" class="search-field" placeholder="Tìm theo tên nông hộ / mã vườn / điều tra viên...">
      <select id="fFormKey"><option value="">Tất cả loại phiếu</option>${FORM_KEYS.map((k) => `<option value="${esc(k)}">${esc(FORM_LABELS[k].label)}</option>`).join('')}</select>
      <select id="fStatus"><option value="">Tất cả trạng thái</option><option value="draft">Nháp</option><option value="submitted">Đã nộp</option><option value="approved">Đã duyệt</option><option value="rejected">Từ chối</option></select>
      ${
        laAdminHeThong
          ? `<select id="fInstance" aria-label="Lọc theo đơn vị điều tra">
               <option value="">Tất cả đơn vị</option>
               ${donVis.map((d) => `<option value="${esc(d.id)}">${esc(d.name)}</option>`).join('')}
             </select>`
          : ''
      }
      <label class="filters-check"><input type="checkbox" id="fHasErrors"> Chỉ phiếu còn lỗi</label>
      <input type="date" id="fDateFrom">
      <input type="date" id="fDateTo">
    </div>
    <div class="card">
      <table class="list-table">
        <thead><tr><th>Loại phiếu</th><th>Hộ / MM</th><th>Mã vườn</th><th>Điều tra viên</th><th>Trạng thái</th><th>Số lỗi audit</th><th>Bắt đầu</th><th>Ngày nộp</th><th></th></tr></thead>
        <tbody id="subBody"></tbody>
      </table>
      <div id="subEmpty" class="empty-state" hidden><span class="ms ms-xl">inbox</span><p>Không có phiếu nào khớp bộ lọc.</p></div>
      <div id="subPaging" class="paging"></div>
    </div>`;

  const ids = ['fFormKey', 'fStatus', 'fHasErrors', 'fDateFrom', 'fDateTo', 'fInstance'];
  // `?.` — `#fInstance` chỉ tồn tại với admin hệ thống.
  ids.forEach((id) =>
    container.querySelector('#' + id)?.addEventListener('change', () => load(1)),
  );
  // Gõ tới đâu lọc tới đó, cùng nhịp 350ms như ô tìm hộ (household-search.js).
  container.querySelector('#fSearch').addEventListener('input', debounce(() => load(1), 350));

  if (canExport) {
    // Xuất theo đúng bộ lọc đang hiển thị, để người dùng không phải khai báo lại
    // điều kiện lần thứ hai và không bao giờ tải về tập khác với tập đang xem.
    const runExport = async (format, btn) => {
      const label = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="ms">hourglass_top</span>Đang tạo file...';
      try {
        const { blob, filename, omittedRepeats } = await api.exportApi.submissions({
          format,
          formKey: container.querySelector('#fFormKey').value || undefined,
          status: container.querySelector('#fStatus').value || undefined,
          // Xuất theo đúng bộ lọc đang hiện — kể cả bộ lọc đơn vị. Một ô lọc, hai
          // đường dùng: danh sách và file xuất không bao giờ lệch nhau.
          instanceId: container.querySelector('#fInstance')?.value || undefined,
          search: container.querySelector('#fSearch').value.trim() || undefined,
          dateFrom: toIsoStart(container.querySelector('#fDateFrom').value),
          dateTo: toIsoEnd(container.querySelector('#fDateTo').value),
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.remove();
        }, 400);
        toast(`Đã tải xuống ${filename}`);
        // Nói ngay, đừng để người dùng mở file ra mới phát hiện thiếu. CSV chỉ có
        // một bảng nên các nhóm lặp (phân bón, cây trồng xen…) không có chỗ để nằm.
        if (omittedRepeats?.length) {
          toast(
            `Lưu ý: file CSV không chứa ${omittedRepeats.length} nhóm lặp ` +
              `(${omittedRepeats.join(', ')}). Xuất XLSX nếu cần chúng.`,
            { ms: 8000 },
          );
        }
      } catch (err) {
        toast('Không xuất được: ' + err.message);
      } finally {
        btn.disabled = false;
        btn.innerHTML = label;
      }
    };
    container.querySelector('#exportCsvBtn').addEventListener('click', (e) => runExport('csv', e.currentTarget));
    container.querySelector('#exportXlsxBtn').addEventListener('click', (e) => runExport('xlsx', e.currentTarget));
  }

  /**
   * Chỉ là lớp che nút cho gọn màn hình — server mới là nơi quyết định (xem
   * SubmissionService.remove). Giữ hai bên cùng một luật: điều tra viên chỉ xoá
   * phiếu do chính mình tạo, giám sát viên/quản trị xoá được mọi phiếu họ nhìn thấy.
   */
  function canDelete(s) {
    if (!user) return false;
    if (user.role === 'enumerator') return s.enumeratorId === user.id;
    // Danh sách cho phép, không phải danh sách chặn: phiên đăng nhập cũ còn lưu
    // vai trò đã bị khai tử (`viewer`) thì không được rơi vào nhánh "được xoá".
    return QUAN_LY.includes(user.role);
  }

  async function askDelete(s, btn) {
    const what = `${formLabel(s.formKey)} — ${subjectText(s)}` +
      (s.plotCode ? ` (vườn ${s.plotCode})` : '');
    // Xoá hẳn: kéo theo cả câu trả lời, kết quả audit và lịch sử thao tác của phiếu.
    // Nói thẳng điều đó ra trước khi hỏi, thay vì một câu "Bạn có chắc không?".
    const ok = window.confirm(
      `Xoá hẳn phiếu:\n\n${what}\nTrạng thái: ${s.status}\n\n` +
      `Toàn bộ câu trả lời, kết quả audit và lịch sử của phiếu sẽ bị xoá vĩnh viễn và KHÔNG khôi phục được.\n\nTiếp tục?`,
    );
    if (!ok) return;
    btn.disabled = true;
    try {
      await api.submissions.remove(s.id);
      toast('Đã xoá phiếu');
      load(currentPage);
    } catch (err) {
      btn.disabled = false;
      toast('Không xoá được: ' + err.message);
    }
  }

  let currentPage = 1;

  async function load(page) {
    currentPage = page;
    const body = container.querySelector('#subBody');
    const empty = container.querySelector('#subEmpty');
    body.innerHTML = `<tr><td colspan="9">Đang tải...</td></tr>`;
    empty.hidden = true;
    const params = {
      page,
      pageSize: 20,
      formKey: container.querySelector('#fFormKey').value || undefined,
      status: container.querySelector('#fStatus').value || undefined,
      instanceId: container.querySelector('#fInstance')?.value || undefined,
      search: container.querySelector('#fSearch').value.trim() || undefined,
      hasErrors: container.querySelector('#fHasErrors').checked ? 'true' : undefined,
      dateFrom: container.querySelector('#fDateFrom').value || undefined,
      dateTo: container.querySelector('#fDateTo').value || undefined,
    };
    try {
      const res = await api.submissions.list(params);
      // Xoá dòng cuối cùng của trang cuối sẽ để lại một trang rỗng — lùi về trang
      // trước thay vì báo "không có phiếu nào khớp bộ lọc", điều đó là sai sự thật.
      if (page > 1 && !res.data.length && res.meta.total > 0) return load(page - 1);
      body.innerHTML = '';
      if (!res.data.length) empty.hidden = false;
      res.data.forEach((s) => {
        const tr = el('tr');
        tr.innerHTML = `
          <td>${esc(formLabel(s.formKey))}</td>
          <td>${esc(subjectText(s))}</td>
          <td class="mono">${esc(s.plotCode || '—')}</td>
          <td>${esc(s.enumeratorName || '—')}</td>
          <td>${statusBadge(s.status)}</td>
          <td>${s.errorCount > 0 ? `<span class="badge status-rejected">${s.errorCount} lỗi</span>` : '—'}</td>
          <td>${formatDate(s.startedAt)}</td>
          <td>${formatDateTime(s.submittedAt)}</td>
          <td class="cell-actions">
            <a href="${buildHash(`/submissions/${s.id}`)}">Mở</a>
            ${canDelete(s) ? `<button class="icon-btn danger" data-act="del" title="Xoá hẳn phiếu này"><span class="ms">delete_forever</span></button>` : ''}
          </td>`;
        tr.querySelector('[data-act="del"]')?.addEventListener('click', (e) => askDelete(s, e.currentTarget));
        body.appendChild(tr);
      });
      renderPaging(res.meta, load);
    } catch (err) {
      body.innerHTML = `<tr><td colspan="9">Lỗi: ${esc(err.message)}</td></tr>`;
    }
  }

  function renderPaging(meta, loadFn) {
    const host = container.querySelector('#subPaging');
    host.innerHTML = '';
    if (meta.totalPages <= 1) return;
    const prev = el('button', 'btn btn-text sm', 'Trước');
    prev.disabled = meta.page <= 1;
    prev.addEventListener('click', () => loadFn(meta.page - 1));
    const info = el('span', 'paging-info', `Trang ${meta.page}/${meta.totalPages} (${meta.total} phiếu)`);
    const next = el('button', 'btn btn-text sm', 'Sau');
    next.disabled = meta.page >= meta.totalPages;
    next.addEventListener('click', () => loadFn(meta.page + 1));
    host.append(prev, info, next);
  }

  load(1);
}

// `<input type="date">` trả về 'YYYY-MM-DD'; API nhận ISO datetime và so sánh với
// `submitted_at`, nên phải nới hai đầu ra cả ngày, nếu không phiếu nộp buổi chiều
// của đúng ngày `dateTo` sẽ bị rơi khỏi kết quả.
function toIsoStart(v) {
  return v ? new Date(`${v}T00:00:00`).toISOString() : undefined;
}
function toIsoEnd(v) {
  return v ? new Date(`${v}T23:59:59.999`).toISOString() : undefined;
}

/** Các loại phiếu điều tra trên một loại chủ thể, theo thứ tự trong danh mục. */
function formKeysFor(subject) {
  return FORM_KEYS.filter((k) => FORM_SUBJECT[k].subject === subject);
}

function formKeyOptionsHtml(keys, preset) {
  return keys
    .map((k) => `<option value="${esc(k)}" ${preset === k ? 'selected' : ''}>${esc(FORM_LABELS[k].label)}</option>`)
    .join('');
}

/**
 * Làm mờ loại phiếu chưa có bộ câu hỏi đang áp dụng (ba phiếu RCP trước khi admin phát
 * hành) — chọn vào chỉ để nhận lỗi NOT_FOUND lúc bấm tạo. Lỗi khác (mất mạng, vai trò
 * không được đọc phiên bản) thì để nguyên: server vẫn là nơi quyết định cuối.
 */
async function markUnpublished(select) {
  const options = Array.from(select.options).filter((o) => o.value);
  const results = await Promise.allSettled(options.map((o) => api.forms.active(o.value)));
  results.forEach((r, i) => {
    if (r.status === 'rejected' && r.reason?.code === 'NOT_FOUND') {
      const o = options[i];
      o.disabled = true;
      o.textContent = `${o.textContent} (chưa có bộ câu hỏi)`;
      if (o.selected) select.value = '';
    }
  });
  select.dispatchEvent(new Event('change'));
}

/**
 * Tạo phiếu: chủ thể trước, loại phiếu sau. Loại phiếu nào đi với chủ thể nào lấy từ
 * `FORM_SUBJECT` của @farmer/audit-rules — cùng bảng API dùng để từ chối chủ thể sai,
 * nên ô chọn ở đây không bao giờ đưa ra một tổ hợp mà server sẽ trả 400.
 */
export async function renderSubmissionNew(container) {
  const { params } = parseHash();
  if (params.middlemanId) return renderNewForMiddleman(container, params);
  if (params.householdId) return renderNewForHousehold(container, params);
  return renderSubjectChooser(container, params);
}

/** Chưa có chủ thể: chọn Hộ (qua màn tìm hộ) hoặc chọn Đại lý/MM ngay tại đây. */
async function renderSubjectChooser(container, params) {
  const formKeyPreset = params.formKey || '';
  container.innerHTML = `
    <div class="page-head"><div><h1>Tạo phiếu điều tra mới</h1><p>Chọn đối tượng điều tra trước.</p></div></div>
    <div class="two-col">
      <div class="card">
        <h2 class="card-title"><span class="ms">home</span>Nông hộ</h2>
        <p class="hint-text">${formKeysFor('household').map((k) => esc(FORM_LABELS[k].label)).join(' · ')}</p>
        <p class="hint-text">Mở hồ sơ hộ, kiểm tra thông tin rồi bấm <b>Tạo phiếu</b> ngay trong hồ sơ.</p>
        <a class="btn" href="${buildHash('/households')}">Tìm hộ</a>
      </div>
      <div class="card">
        <h2 class="card-title"><span class="ms">storefront</span>Đại lý / MM</h2>
        <p class="hint-text">${formKeysFor('middleman').map((k) => esc(FORM_LABELS[k].label)).join(' · ')}</p>
        <form id="pickMmForm">
          <div class="form-row">
            <label for="mmSelect">Đại lý / MM *</label>
            <select id="mmSelect" required><option value="">Đang tải danh mục...</option></select>
          </div>
          <button class="btn" type="submit">Tiếp tục</button>
        </form>
      </div>
    </div>`;

  const mmSelect = container.querySelector('#mmSelect');
  try {
    const options = await api.middlemen.options();
    mmSelect.innerHTML = options.length
      ? `<option value="">— Chọn —</option>${options
          .map((m) => `<option value="${esc(m.id)}">${esc(m.code)} · ${esc(m.name)}</option>`)
          .join('')}`
      : '<option value="">Đơn vị chưa có MM nào trong danh mục</option>';
  } catch (err) {
    mmSelect.innerHTML = `<option value="">${esc(err.message)}</option>`;
  }
  container.querySelector('#pickMmForm').addEventListener('submit', (e) => {
    e.preventDefault();
    if (!mmSelect.value) return;
    navigate(buildHash('/submissions/new', { middlemanId: mmSelect.value, ...(formKeyPreset ? { formKey: formKeyPreset } : {}) }));
  });
}

async function renderNewForMiddleman(container, params) {
  container.innerHTML = `<div class="empty-state">Đang tải thông tin MM...</div>`;
  let mm;
  try {
    mm = await api.middlemen.get(params.middlemanId);
  } catch (err) {
    container.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
    return;
  }
  const keys = formKeysFor('middleman');
  const preset = keys.includes(params.formKey) ? params.formKey : keys.length === 1 ? keys[0] : '';
  container.innerHTML = `
    <div class="page-head"><div><h1>Tạo phiếu điều tra mới</h1><p>Cho Đại lý/MM: <b>${esc(mm.code)} · ${esc(mm.name)}</b></p></div></div>
    <div class="card">
      <form id="newSubForm">
        <div class="form-row">
          <label for="formKeySelect">Loại phiếu *</label>
          <select name="formKey" id="formKeySelect" required>
            <option value="">— Chọn —</option>
            ${formKeyOptionsHtml(keys, preset)}
          </select>
        </div>
        <div class="error-text" id="newSubError" hidden></div>
        <button class="btn" type="submit">Tạo phiếu &amp; bắt đầu nhập</button>
      </form>
    </div>`;

  const errBox = container.querySelector('#newSubError');
  markUnpublished(container.querySelector('#formKeySelect'));
  container.querySelector('#newSubForm').addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.hidden = true;
    try {
      const sub = await api.submissions.create({ formKey: container.querySelector('#formKeySelect').value, middlemanId: mm.id });
      toast('Đã tạo phiếu mới');
      navigate(buildHash(`/submissions/${sub.id}`));
    } catch (err) {
      errBox.textContent = err.message;
      errBox.hidden = false;
    }
  });
}

/**
 * Phiếu cho Hộ chỉ tạo từ hồ sơ hộ (yêu cầu 2026-11-01): điều tra viên mở hồ sơ, kiểm thông
 * tin — thấy sai thì "Báo sai thông tin" — rồi mới bấm Tạo phiếu ngay ở đó. Đường dẫn cũ
 * `/submissions/new?householdId=` (dấu trang, `returnTo` của màn Thêm vườn) chuyển về hồ sơ
 * và tự mở hộp thoại, giữ Vườn / loại phiếu đã chọn.
 */
function renderNewForHousehold(container, params) {
  container.innerHTML = '';
  navigate(
    buildHash(`/households/${params.householdId}`, {
      newSubmission: '1',
      ...(params.plotId ? { plotId: params.plotId } : {}),
      ...(params.formKey ? { formKey: params.formKey } : {}),
    }),
  );
}

/**
 * Hộp thoại tạo phiếu cho một Hộ, mở từ hồ sơ hộ. Loại phiếu lấy từ `FORM_SUBJECT` — chỉ
 * phiếu của Hộ; Vườn bắt buộc / tuỳ chọn / không có theo từng loại.
 *
 * @param {object} household  kết quả `GET /households/:id` (cần `plots`)
 * @param {{formKey?: string, plotId?: string}} [preset]
 */
export function openHouseholdSubmissionDialog(household, preset = {}) {
  const householdId = household.id;
  const keys = formKeysFor('household');
  const formKeyPreset = keys.includes(preset.formKey) ? preset.formKey : '';
  const plotPreset = household.plots.some((p) => p.id === preset.plotId) ? preset.plotId : '';

  openFormDialog({
    title: `Tạo phiếu cho hộ ${household.fullName}`,
    submitLabel: 'Tạo phiếu & bắt đầu nhập',
    submitIcon: 'note_add',
    bodyHtml: `
      <p class="hint-text">Phiếu tham chiếu thông tin hộ và vườn ở hồ sơ. Thông tin sai thì đóng hộp thoại,
        bấm <b>Báo sai thông tin</b> trước — vẫn tạo phiếu được trong lúc chờ duyệt.</p>
      <div class="form-row">
        <label for="formKeySelect">Loại phiếu *</label>
        <select name="formKey" id="formKeySelect" required>
          <option value="">— Chọn —</option>
          ${formKeyOptionsHtml(keys, formKeyPreset)}
        </select>
        <span class="hint-text" id="formKeyHint"></span>
      </div>
      <div class="form-row" id="plotRow">
        <label id="plotLabel" for="plotSelect">Vườn</label>
        <select name="plotId" id="plotSelect">
          <option value="">— Chưa chọn vườn cụ thể —</option>
          ${household.plots
            .map((p) => `<option value="${esc(p.id)}" ${p.id === plotPreset ? 'selected' : ''}>${esc(p.plotCode || p.id)} — ${esc(p.areaHa)} ha</option>`)
            .join('')}
        </select>
        <span class="hint-text" id="plotHint"></span>
        ${
          !household.plots.length
            ? `<span class="hint-text">Hộ này chưa có Vườn — <a href="${buildHash('/plots/new', { householdId, returnTo: '/submissions/new' })}">tạo vườn mới</a>.</span>`
            : ''
        }
      </div>`,
    onMount: (form) => {
      const formKeySelect = form.querySelector('#formKeySelect');
      const plotSelect = form.querySelector('#plotSelect');
      /** `required` / `optional` / `forbidden` theo FORM_SUBJECT — chưa chọn loại phiếu thì như `optional`. */
      const sync = () => {
        const rule = FORM_SUBJECT[formKeySelect.value]?.plot ?? 'optional';
        form.querySelector('#formKeyHint').textContent = FORM_LABELS[formKeySelect.value]?.description || '';
        // Phiếu cấp hộ (RCP hộ) không gắn lô — ẩn hẳn ô và bỏ lựa chọn cũ, nếu không API trả 400.
        form.querySelector('#plotRow').hidden = rule === 'forbidden';
        if (rule === 'forbidden') plotSelect.value = '';
        plotSelect.required = rule === 'required';
        form.querySelector('#plotLabel').textContent = rule === 'required' ? 'Vườn *' : 'Vườn';
        form.querySelector('#plotHint').textContent =
          rule === 'required' ? 'Loại phiếu này bắt buộc gắn một Vườn (lô) của hộ.' : '';
      };
      formKeySelect.addEventListener('change', sync);
      sync();
      void markUnpublished(formKeySelect);
    },
    onSubmit: async (form) => {
      const formKey = form.formKey.value;
      if (!formKey) return 'Chọn loại phiếu.';
      const rule = FORM_SUBJECT[formKey].plot;
      const plotId = rule === 'forbidden' ? undefined : form.plotId.value || undefined;
      if (rule === 'required' && !plotId) return `Phiếu "${FORM_LABELS[formKey].label}" bắt buộc phải chọn Vườn.`;
      try {
        const sub = await api.submissions.create({ formKey, householdId, plotId });
        toast('Đã tạo phiếu mới');
        navigate(buildHash(`/submissions/${sub.id}`));
      } catch (err) {
        return err.message;
      }
    },
  });
}
